import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormGroup, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Select, Store } from '@ngxs/store';
import { BehaviorSubject, Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, take, withLatestFrom } from 'rxjs/operators';
import { AUDIT_TIME_LIST } from '../reports/components/dea-reports/constants/constants';
import { MM_DD_YYYY, MS_IN_A_DAY } from '../shared/constants/constants';
import { BaseTableDirective } from '../shared/directives/base-table.directive';
import { getFormattedDateRangeString, highlightRequiredControlsWithErrors, todayAtMidnight } from '../shared/helpers/helpers';
import { AuditTimeTypeEnum } from '../shared/interfaces/AuditTimeTypeEnum';
import { BadgeColorsEnum } from '../shared/interfaces/BadgeColorsEnum';
import { AuditStatusesEnum, AuditTypesEnum, IAudit } from '../shared/interfaces/IAudit';
import { ICreateAuditPayload } from '../shared/interfaces/ICreateAuditPayload';
import { IOptionItem } from '../shared/interfaces/option-item.interface';
import { SearchDateRange } from '../shared/interfaces/SearchDateRange';
import { CreateAudit, DeleteAudit, GetAudits, SaveAuditsFilters, SetAuditsList } from '../shared/states/audits/audits.actions';
import { AuditsState } from '../shared/states/audits/audits.state';
import { AuthState } from '../shared/states/auth/auth.state';
import { PharmacyState } from '../shared/states/pharmacy/pharmacy.state';
import { AuditsService } from './audits.service';
import { SearchInputColorEnum } from '../shared/components/search-input/search-input.component';
import { IFilterAuditsCriteria } from './interfaces/filter-audits-criteria.interface';
@UntilDestroy()
@Component({
  selector: 'app-audits',
  templateUrl: './audits.component.html',
  styleUrls: ['./audits.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AuditsComponent extends BaseTableDirective implements OnInit, OnDestroy {

  tableColumns = {
    createDate: false,
    type: true,
    lineItemsCount: true,
    status: true,
    completeDate: true
  };

  AuditTypesEnum = AuditTypesEnum;
  AuditStatusesEnum = AuditStatusesEnum;
  BadgeColorsEnum = BadgeColorsEnum;
  SearchInputColorEnum = SearchInputColorEnum;
  
  @Select(AuditsState.loading) loading$: Observable<boolean>;
  @Select(AuditsState.success) success$: Observable<boolean>;
  @Select(AuditsState.audits) audits$: Observable<IAudit[]>;
  @Select(AuditsState.auditWithDetails) emptyAuditWithId$: Observable<IAudit>;
  @Select(PharmacyState.enableC3toC5Drugs) enableC3toC5Drugs$: Observable<boolean>;
  @Select(PharmacyState.inventoryLocationsEnabled) inventoryLocationsEnabled$: Observable<boolean>;
  @Select(AuditsState.filtersForm) filtersForm$: Observable<any>;


  displayCreateAuditTypeSelector: boolean;
  selectedAuditType: string | number = -1;
  customDateRangeStartDate: string | Date;
  customDateRangeEndDate: string | Date;
  selectedSearchDateRange: SearchDateRange;
  auditsList: IAudit[] = [];

  
  displayNoDrugsError$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  pendingDeleteAuditModal$: BehaviorSubject<{show: boolean, id: number}> = new BehaviorSubject(null);

  auditTypesList: IOptionItem[] = [
    {
      id: AuditTypesEnum.AdHoc,
      name: 'Ad Hoc',
      description: 'Pick any particular drugs to audit'
    },
    {
      id: AuditTypesEnum.ActiveDrugs,
      name: 'Active Drugs',
      description: 'Audit drugs that were active over a certain time period'
    },
    {
      id: AuditTypesEnum.InactiveDrugs,
      name: 'Inactive Drugs',
      description: 'Audit drugs with no activity over a certain time period'
    },
    {
      id: AuditTypesEnum.AllDrugs,
      name: 'All Drugs',
      description: 'Audit your entire inventory'
    },
    {
      id: AuditTypesEnum.NinetyDayAudit,
      name: '90 Day Audit Report',
      description: 'Point in time audit for all drugs starting from 90 day prior'
    },
    {
      id: AuditTypesEnum.TotalsOnly,
      name: 'Totals Only Audit',
      description: 'Point in time audit for all drugs'
    },
  ];
  
  backCountFilterOption: IOptionItem = {
    id: AuditTypesEnum.BackCount,
    name: 'Back Count',
  };

  auditTypesWithBackCountList: IOptionItem[] = [
    ...this.auditTypesList, 
    this.backCountFilterOption
  ]

  periodsList = [
    {id: SearchDateRange.Custom, name: 'Custom Range'},
    {id: SearchDateRange.Last90Days, name: 'Last 90 days'},
    {id: SearchDateRange.Last30Days, name: 'Last 30 days'},
    {id: SearchDateRange.Today, name: 'Today'},
    {id: SearchDateRange.ThisWeek, name: 'This week'},
    {id: SearchDateRange.LastWeek, name: 'Last week'},
    {id: SearchDateRange.ThisMonth, name: 'This month'},
    {id: SearchDateRange.LastMonth, name: 'Last month'},
    {id: SearchDateRange.ThisQuarter, name: 'This quarter'},
    {id: SearchDateRange.LastQuarter, name: 'Last quarter'},
    {id: SearchDateRange.ThisYear, name: 'This year'},
    {id: SearchDateRange.LastYear, name: 'Last year'},
  ];

  periodsListFor90DayAudit = [  
    {id: SearchDateRange.Custom, name: 'Custom'},
  ];

  auditTimeList = AUDIT_TIME_LIST;
  ninetyDayAuditTimeForm: UntypedFormGroup;
  schedules = new UntypedFormControl(null);
  inventoryLocations = new UntypedFormControl(null);
  filterAuditsForm: FormGroup;
  createAuditForm: FormGroup;

  displayTimePeriodDropdown: boolean;
  display90DayAuditOptions: boolean;
  todayDate: Date;
  nDaysPriorToday: Date;
  
  startDateDisabledTimeTypes = [];
  endDateDisabledTimeTypes = [];

  c3c5Enabled: boolean;

  navigationState: any;

  @ViewChild('customPeriodDatepicker') customPeriodDatepicker;
  @ViewChild('auditTypesDropdown') auditTypesDropdown;
  @ViewChild('periodDropdown') periodDropdown;

  constructor(
    protected store: Store, 
    private datePipe: DatePipe,
    private router: Router,
    private fb: UntypedFormBuilder,
    private cd: ChangeDetectorRef,
    private auditService: AuditsService
    ) { 
      super(store); 
      this.ninetyDayAuditTimeForm = this.fb.group({
        startDateType: [AuditTimeTypeEnum.EndOfDay],
        endDateType: [AuditTimeTypeEnum.EndOfDay]
      });

      this.todayDate = new Date(); 
      this.todayDate = new Date(this.todayDate.setHours(23, 59, 59, 59));

      this.filterAuditsForm = fb.group({
        searchValue: '',
        auditTypes: []
      });

      this.createAuditForm = fb.group({
        type: ['', Validators.required],
        name: ''
      });
      this.navigationState = this.router.getCurrentNavigation()?.extras?.state;

      this.handleNavigationFromBackCountPill();
    }

  ngOnInit(): void {
    this.filtersForm$
    .pipe(take(1))
    .subscribe(val => {
      if (val) {
        this.filterAuditsForm.patchValue(val);
      }
    })

    this.getAudits();
    
    this.enableC3toC5Drugs$
    .pipe(take(1))
    .subscribe(x => this.c3c5Enabled = x);

    this.filterAuditsForm.get('searchValue').valueChanges
    .pipe(untilDestroyed(this), debounceTime(200), distinctUntilChanged())
    .subscribe(val => {
      this.getAudits();
    });
    this.filterAuditsForm.get('auditTypes').valueChanges
    .pipe(untilDestroyed(this), debounceTime(200), distinctUntilChanged())
    .subscribe(val => {
      this.getAudits();
    });
  }

  ngOnDestroy(): void {
    this.store.dispatch(new SaveAuditsFilters(this.filterAuditsForm.getRawValue()));
  }


  createAuditWithSelectedType() {
    if (this.createAuditForm.invalid) {
      highlightRequiredControlsWithErrors(this.createAuditForm);
      return;
    }
    const payload: ICreateAuditPayload = {
      selectedType: this.selectedAuditType,
      customDateRangeStartDate: this.customDateRangeStartDate,
      customDateRangeEndDate: this.customDateRangeEndDate,
      userId: this.store.selectSnapshot(AuthState)?.currentUser?.id,
      searchDateRange: this.selectedSearchDateRange || SearchDateRange.All,
      offset: new Date().getTimezoneOffset() / 60,
      schedules: this.schedules?.value?.map(x => x.id) || null,
      inventoryLocations: this.inventoryLocations?.value?.map(x => x.id) || null
     }

    if(this.selectedAuditType === AuditTypesEnum.NinetyDayAudit || this.selectedAuditType === AuditTypesEnum.TotalsOnly) { 
      payload.startDateType = this.ninetyDayAuditTimeForm.get('startDateType').value;
      payload.endDateType = this.ninetyDayAuditTimeForm.get('endDateType').value;
    }

    if (this.selectedAuditType ===  AuditTypesEnum.TotalsOnly) {
      payload.name = this.createAuditForm.get('name').value
    }

    this.displayNoDrugsError$.next(false);
    this.store.dispatch(new CreateAudit(payload))
    .pipe(take(1), withLatestFrom(this.success$, this.emptyAuditWithId$))
    .subscribe(([_, success, emptyAuditWithId]) => {
      if(success) {
        if(!this.store.selectSnapshot(AuditsState)?.createAuditNoDrugsResponse) 
          this.goToDetails(emptyAuditWithId.id);
        else 
        this.displayNoDrugsError$.next(true);
      }
    })
  }

  auditTypeSelected(value: IOptionItem): void {
   this.displayNoDrugsError$.next(false);
   this.selectedSearchDateRange = SearchDateRange.All;
   this.customDateRangeStartDate = null;
   this.customDateRangeEndDate = null;
   this.displayTimePeriodDropdown = value?.id === AuditTypesEnum.ActiveDrugs || value?.id === AuditTypesEnum.InactiveDrugs;
   this.display90DayAuditOptions = value?.id === AuditTypesEnum.NinetyDayAudit || value?.id === AuditTypesEnum.TotalsOnly;
   this.selectedAuditType = value?.id;

   if(this.display90DayAuditOptions) {
    this.afterNinetyDayOrTotalsAuditSelected();
   }
  }

  afterNinetyDayOrTotalsAuditSelected() {
   setTimeout(() => {
    if (this.selectedAuditType === AuditTypesEnum.NinetyDayAudit) {
      const auditAge = this.initNinetyDayAuditTimeValuesAndGetAuditAge();
      this.selectLastNDayAudit(auditAge);
    } else if (this.selectedAuditType === AuditTypesEnum.TotalsOnly) {
      const auditAge = 365;
      this.selectLastNDayAudit(auditAge);
      this.nDaysPriorToday = new Date(2016, 1, 1);
    }
  
   }); 
    const disableAll = [AuditTimeTypeEnum.BeginningOfDay, AuditTimeTypeEnum.EndOfDay];
    this.startDateDisabledTimeTypes = disableAll;
    this.endDateDisabledTimeTypes = disableAll;
    this.auditService.isInventoryModifiedToday()
    .pipe(untilDestroyed(this))
    .subscribe(response => { 
      if(response?.value) {
        this.endDateDisabledTimeTypes = [AuditTimeTypeEnum.BeginningOfDay];
      } else {
        this.endDateDisabledTimeTypes = [];
      }
      this.startDateDisabledTimeTypes = [];
      this.cd.detectChanges();
    });
  }

  initNinetyDayAuditTimeValuesAndGetAuditAge(): number { 
    const today = new Date();
    //ignore time
    const pharmacyCreateDate = new Date(new Date(this.store.selectSnapshot(PharmacyState.currentPharmacy)?.createDate).toDateString());
    const howManyDaysAgoPharmacyCreated = Math.floor((today.getTime() - pharmacyCreateDate.getTime()) / MS_IN_A_DAY);
    const auditAge = howManyDaysAgoPharmacyCreated < 90 ? howManyDaysAgoPharmacyCreated : 90;

    this.nDaysPriorToday = new Date(today.setDate(today.getDate() - auditAge));
    this.nDaysPriorToday = new Date(this.nDaysPriorToday.setHours(0, 0, 0, 0));
    return auditAge;
  }

  dropAudit(id: number): void {
    this.pendingDeleteAuditModal$.next({show: true, id: id});
  }

  deletingConfirmed(id: number) {
    this.store.dispatch(new DeleteAudit(id)).pipe(untilDestroyed(this))
    this.pendingDeleteAuditModal$.next(null);
  }

  customPeriodChanged(date): void {
    const selectedAuditType = this.auditTypesList.find(x => x.id === this.selectedAuditType);
    const typeName = `Custom (${this.datePipe.transform(date.value[0], MM_DD_YYYY)} - ${this.datePipe.transform(date.value[1], MM_DD_YYYY)})`;
    this.periodDropdown.chooseOption({id: SearchDateRange.Custom, name: typeName, description: selectedAuditType?.description });
    //TODO: make sure valid in TEST
    this.customDateRangeStartDate = date.value[0].toDateString();
    this.customDateRangeEndDate = date.value[1].toDateString();
  }

  startDateChanged(date): void { 
    const lastNDays = Math.floor((new Date().getTime() - new Date(date.value).getTime()) / MS_IN_A_DAY);
    this.selectLastNDayAudit(lastNDays);
  }
  
  goToDetails(id: number): void {
    this.router.navigate(['audits/details', id]);
  }

  selectLastNDayAudit(n: number): void {
    const today = new Date();
    const lastNDays = new Date(today.setDate(today.getDate() - n));  
    this.periodDropdown.initWithChosenOption({id: SearchDateRange.Custom, name: `${this.datePipe.transform(lastNDays, MM_DD_YYYY)}` });
    this.customDateRangeStartDate = lastNDays.toDateString();
    this.customDateRangeEndDate = new Date().toDateString();
    this.selectedSearchDateRange = SearchDateRange.Custom;
    this.cd.detectChanges();
  }

  sortColumn(column: string) {
    this.store.dispatch(new SetAuditsList(this.sort(this.auditsList, column)));
  }

  changePeriod(period): void {
    this.displayNoDrugsError$.next(false);
    if (period && period.id === SearchDateRange.Custom) {
      setTimeout(() => this.customPeriodDatepicker?.open());
    } else {
      this.resetCustomRange();
    }
    this.selectedSearchDateRange = period?.id;
  }

  getDateRange(from: string, to: string): string {
    return getFormattedDateRangeString(from, to, MM_DD_YYYY, null, '-');
  }

  createAuditDisabled(): boolean {
    if ((this.selectedAuditType === AuditTypesEnum.ActiveDrugs || this.selectedAuditType === AuditTypesEnum.InactiveDrugs || this.selectedAuditType === AuditTypesEnum.NinetyDayAudit
      ) && this.store.selectSnapshot(PharmacyState.enableC3toC5Drugs)) {
      return this.schedules.invalid;
    }
    return this.selectedAuditType === undefined || this.selectedAuditType === null || this.selectedAuditType as AuditTypesEnum <= -1;
  }


  openCreateAuditModal(): void {
    this.display90DayAuditOptions = null;
    this.displayTimePeriodDropdown = null;
    this.selectedAuditType = null;
    this.displayCreateAuditTypeSelector = true;
  }

  resetCustomRange(): void {
    const customRange = this.periodsList.find(x => x.id === SearchDateRange.Custom);
    (customRange as any).value = null;
    this.customDateRangeStartDate = null;
    this.customDateRangeEndDate = null;
    this.customPeriodDatepicker?.reset()
   }


  cancelAuditCreation(): void {
    this.resetCustomRange();
    this.selectedSearchDateRange = null;
    this.periodDropdown?.reset();
    this.displayCreateAuditTypeSelector = false;
    this.selectedAuditType = null;
    this.schedules.patchValue(null);
    this.inventoryLocations.patchValue(null);
    
    const typeControl = this.createAuditForm.get('type');
    typeControl.patchValue(null);
    typeControl.markAsUntouched();
    typeControl.markAsPristine();
    this.createAuditForm.updateValueAndValidity();
  }

  private getAudits(): void {
    const raw = this.filterAuditsForm.getRawValue();
    var request: IFilterAuditsCriteria = {
      searchValue: raw.searchValue,
      auditTypes: raw.auditTypes?.map(x => x.id)
    };
    this.store.dispatch(new GetAudits(request))
    .pipe(untilDestroyed(this), withLatestFrom(this.audits$))
    .subscribe(([_, audits]) => {
      this.auditsList = audits;
    });
  }

  private handleNavigationFromBackCountPill(): void {
    if (this.navigationState?.prefilterBackCounts) {
      const auditTypes = this.filterAuditsForm.get('auditTypes');
      auditTypes?.patchValue([this.backCountFilterOption]);
      this.store.dispatch(new SaveAuditsFilters(this.filterAuditsForm.getRawValue()));
    }
  }

}

